import { useEffect } from "react";

import { OVERLAY_PREF } from "../generator/constants";
/**
 * Custom hook to manage and restore the cursor position for an input or textarea element.
 *
 * @param id - A unique identifier for the input element, used to store cursor position in localStorage.
 * @param inputRef - A React ref object pointing to the input or textarea element.
 * The `current` property should be an HTMLInputElement or HTMLTextAreaElement.
 * @returns An object containing event handlers for focus, blur, and cursor position management.
 */
const useSavedCursorPosition = (
  id: string,
  inputRef: { current: HTMLInputElement | HTMLTextAreaElement | null },
) => {
  useEffect(() => {
    if (id.includes(OVERLAY_PREF)) return;
    const focused = localStorage.getItem(`focused-${id}`);
    const cursorPosition = localStorage.getItem(`cursorPosition-${id}`);
    if (focused && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
      if (cursorPosition) {
        inputRef.current.setSelectionRange(
          Number(cursorPosition),
          Number(cursorPosition),
        );
      }
    }
  }, [id, inputRef]);
  /**
   * Handles the focus event for the input element.
   * Stores the focus state in localStorage.
   *
   * @param e - The focus event object.
   */
  const handleFocus = (e: React.FocusEvent) => {
    if (e.target === inputRef.current) {
      localStorage.setItem(`focused-${id}`, "true");
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (e.target === inputRef.current) {
      localStorage.removeItem(`focused-${id}`);
      localStorage.removeItem(`cursorPosition-${id}`);
    }
  };

  const setCursorPosition = () => {
    if (inputRef.current) {
      localStorage.setItem(
        `cursorPosition-${id}`,
        inputRef.current.selectionStart?.toString() || "0",
      );
    }
  };

  useEffect(() => {
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener("mouseup", setCursorPosition);
      inputElement.addEventListener("keyup", setCursorPosition);
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener("mouseup", setCursorPosition);
        inputElement.removeEventListener("keyup", setCursorPosition);
      }
    };
  }, [inputRef]);

  return { handleFocus, handleBlur, setCursorPosition };
};

export const useClearCursorPositions = () => {
  return () => {
    Object.keys(localStorage).forEach(key => {
      if (key.includes("cursorPosition") || key.includes("focused")) {
        localStorage.removeItem(key);
      }
    });
  };
};

export default useSavedCursorPosition;
